import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import './Hero.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default hero slides (fallback)
const DEFAULT_SLIDES = [
  {
    image: 'https://images.unsplash.com/photo-1541643600914-78b084683601?w=1920&q=80',
    alt: 'Luxury Perfume Collection',
    show_button: true,
    button_text: null,
    button_link: '/products'
  }
];

const Hero = () => {
  const { t, lang } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [heroSlides, setHeroSlides] = useState(DEFAULT_SLIDES);

  // Fetch hero slides from API
  useEffect(() => {
    const fetchHeroSlides = async () => {
      try {
        const res = await fetch(`${API_URL}/api/homepage/settings`);
        if (res.ok) {
          const data = await res.json();
          if (data.hero_slides && data.hero_slides.length > 0) {
            setHeroSlides(data.hero_slides);
          }
        }
      } catch (err) {
        console.error('Failed to fetch hero slides:', err);
      }
    };
    fetchHeroSlides();
  }, []);

  // Auto-rotate slides every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [heroSlides.length]);

  // Get current slide's per-slide overrides (fall back to translations)
  const currentSlideData = heroSlides[currentSlide] || {};
  const showButton = currentSlideData.show_button !== false;
  const buttonText = (lang === 'bg'
    ? currentSlideData.button_text
    : (currentSlideData.button_text_en || currentSlideData.button_text)) || t('heroButton');
  const buttonLink = currentSlideData.button_link || '/products';

  const titleText = (lang === 'bg'
    ? currentSlideData.title
    : (currentSlideData.title_en || currentSlideData.title)) || t('heroTitle');
  const subtitleText = (lang === 'bg'
    ? currentSlideData.subtitle
    : (currentSlideData.subtitle_en || currentSlideData.subtitle)) || t('heroSubtitle');

  // Check if button link is external
  const isExternal = buttonLink.startsWith('http');

  return (
    <section className="hero" data-testid="hero-section">
      {/* Slides */}
      <div className="hero-slides">
        {heroSlides.map((slide, index) => (
          <div
            key={slide.image || `slide-${index}`}
            className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
            style={{ backgroundImage: `url(${slide.image})` }}
            aria-hidden={index !== currentSlide}
          />
        ))}
      </div>

      {/* Overlay with content */}
      <div className="hero-overlay">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title" data-testid="hero-title">{titleText}</h1>
            <p className="hero-subtitle">{subtitleText}</p>
            {showButton && (
              isExternal ? (
                <a href={buttonLink} className="hero-button" data-testid="hero-button" target="_blank" rel="noopener noreferrer">
                  {buttonText}
                </a>
              ) : (
                <Link to={buttonLink} className="hero-button" data-testid="hero-button">
                  {buttonText}
                </Link>
              )
            )}
          </div>
        </div>
      </div>

      {/* Slide indicators */}
      <div className="hero-indicators">
        {heroSlides.map((slide, index) => (
          <button
            key={slide.image || `indicator-${index}`}
            className={`hero-indicator ${index === currentSlide ? 'active' : ''}`}
            onClick={() => setCurrentSlide(index)}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

export default Hero;
